import { DataTableV2 } from "@platform/design-system";
import { useSignedInUser } from "@platform/user/client";
import { useCallback, useMemo, useState } from "react";

import { adminDraftHeaders, draftHeaders } from "../pages/RFPs/utils";
import { RfpListItem } from "../hooks/useRfpsList";
import { RfpsTableOptions } from "../types/rfpGridContext";
import { isInternalUser } from "../utils";

import ConfirmDialog from "./confirmDialog";

type DraftRFPsTableProps = {
  isBrokerPartner: boolean;
  options?: RfpsTableOptions;
  onDiscardRfpDraft: (id: string | number) => void;
};

export const DraftRFPsTable: React.FC<DraftRFPsTableProps> = ({
  isBrokerPartner,
  options,
  onDiscardRfpDraft,
}) => {
  const { role } = useSignedInUser();
  const internalUser = isInternalUser(role);
  const [isDiscardDialogOpen, setIsDiscardDialogOpen] =
    useState<boolean>(false);
  const [data, setData] = useState<RfpListItem | null>(null);

  const columns = useMemo(
    () => (internalUser && !isBrokerPartner ? adminDraftHeaders : draftHeaders),
    [internalUser, isBrokerPartner],
  );

  const columnsWithFlexStyle = useMemo(
    () =>
      columns.map((col) => {
        if (col.field === "lastModifyDate") {
          if (internalUser && !isBrokerPartner) {
            return { ...col, flex: 0.5 };
          }
          return { ...col, minWidth: 350 };
        }
        return col;
      }),
    [columns, internalUser, isBrokerPartner],
  );

  const handleConfirmChange = useCallback(() => {
    setIsDiscardDialogOpen(false);
    if (data?.id) onDiscardRfpDraft(data.id);
  }, [onDiscardRfpDraft, data]);

  const openDiscardDialog = useCallback(
    (data) => {
      setIsDiscardDialogOpen(true);
      setData(data);
    },
    [setIsDiscardDialogOpen, setData],
  );

  return (
    <>
      <DataTableV2
        columnDefs={columnsWithFlexStyle}
        overlayNoRowsTemplate="No draft RFPs found"
        gridOptions={{ ...options }}
        context={{
          ...options?.context,
          openDiscardDialog,
        }}
      />

      {isDiscardDialogOpen && (
        <ConfirmDialog
          isOpen={isDiscardDialogOpen}
          title={`Discard RFP ${data?.id} Draft`}
          message={`Are you sure you want to discard this draft for ${data?.metaData?.clientName}?`}
          confirmBtnMsg="Yes, Discard"
          onClose={() => setIsDiscardDialogOpen(false)}
          onConfirm={handleConfirmChange}
        />
      )}
    </>
  );
};
